import React, { useState, useRef, useEffect } from "react";
import { Handle, Position, useReactFlow } from "reactflow";
import {
  Zap,
  Minus,
  Plus,
  MoreHorizontal,
  Calendar,
  Edit2,
  X,
  Table2,
  Trash2,
} from "lucide-react";
import "./Node.css";
import "./TriggerNode.css";
import { useWorkflow } from "@/lib/contexts/workflow-context";
import CopyPasteButtons from "./CopyPasteButtons";

const TriggerNode = ({ data, id }) => {
  // Use data.expanded for minimized/expanded state
  const { expanded = true } = data;
  const { currentWorkflow, getNodeTriggers, deleteNode } = useWorkflow();
  const [triggerType, setTriggerType] = useState(
    data.triggerType || "schedule"
  );
  const [schedule, setSchedule] = useState(data.schedule || "0 * * * *");
  const [headers, setHeaders] = useState(
    data.headers || [
      { key: "test", value: "value" },
      { key: "key", value: "value" },
    ]
  );
  const [jsonParams, setJsonParams] = useState(
    data.jsonParams || '{"webhookParam1": "value"}'
  );
  const [showTooltip, setShowTooltip] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showSquareTooltip, setShowSquareTooltip] = useState(false);
  const [showMinimizeTooltip, setShowMinimizeTooltip] = useState(false);
  const reactFlowInstance = useReactFlow();
  const handleRef = useRef(null);
  const isDragging = useRef(false);
  const dragStartTime = useRef(0);
  const [enabledTriggers, setEnabledTriggers] = useState(
    data.enabledTriggers || []
  );

  // Add this state to track if the node was manually toggled
  const [manuallyToggled, setManuallyToggled] = useState(false);
  
  // Add ref to track if triggers have been loaded to prevent infinite loops
  const triggersLoaded = useRef(false);

  // Add effect to handle panning prevention
  useEffect(() => {
    // Function to prevent mouse events that would cause panning
    const preventPanning = (e) => {
      if (document.body.classList.contains("disable-flow-panning")) {
        // Only prevent default on middle mouse button (wheel) or when dragging with left button
        if (
          e.button === 1 ||
          (e.button === 0 && e.type === "mousemove" && e.buttons === 1)
        ) {
          e.stopPropagation();
        }
      }
    };

    // Add event listeners to the document
    document.addEventListener("mousedown", preventPanning, {
      capture: true,
      passive: false,
    });
    document.addEventListener("mousemove", preventPanning, {
      capture: true,
      passive: false,
    });

    // Clean up event listeners on component unmount
    return () => {
      document.removeEventListener("mousedown", preventPanning, {
        capture: true,
        passive: false,
      });
      document.removeEventListener("mousemove", preventPanning, {
        capture: true,
        passive: false,
      });
    };
  }, []);

  // Load triggers from database when node mounts or workflow changes
  useEffect(() => {
    const loadTriggersFromDatabase = async () => {
      // Only load from database if triggers are not already provided and we haven't loaded them before
      if (data.workflowId && id && !triggersLoaded.current && (!data.enabledTriggers || data.enabledTriggers.length === 0)) {
        try {
          triggersLoaded.current = true; // Mark as loaded to prevent multiple calls
          const dbTriggers = await getNodeTriggers(data.workflowId, id);
          
          if (dbTriggers && dbTriggers.length > 0) {
            // Convert database triggers to enabled triggers format
            const enabledDbTriggers = dbTriggers
              .filter(trigger => trigger.enabled)
              .map(trigger => ({
                id: `${trigger.trigger_type}-${trigger.id}`,
                type: trigger.trigger_type,
                name: trigger.name || trigger.trigger_type,
                description: trigger.description || '',
                enabled: trigger.enabled,
                config: trigger.options || {}
              }));
            
            setEnabledTriggers(enabledDbTriggers);
            
            // Update the node data through the parent if callback is available
            if (data.onNodeDataChange) {
              data.onNodeDataChange(id, { enabledTriggers: enabledDbTriggers });
            }
          }
        } catch (error) {
          // Only reset on non-network errors to allow retry
          if (error.name !== 'TypeError' || !error.message.includes('Failed to fetch')) {
            console.error('Failed to load triggers from database:', error);
            triggersLoaded.current = false; // Reset on error so it can retry
          } else {
            // For network errors, retry after a delay
            setTimeout(() => {
              triggersLoaded.current = false;
            }, 2000); // Retry after 2 seconds
          }
        }
      }
    };

    loadTriggersFromDatabase();
  }, [data.workflowId, id, data.enabledTriggers]); // Add data.enabledTriggers to dependencies

  // Update enabledTriggers when data.enabledTriggers changes (from parent)
  useEffect(() => {
    if (data.enabledTriggers !== undefined) {
      setEnabledTriggers(data.enabledTriggers);
    }
  }, [data.enabledTriggers]);

  const addHeader = () => {
    setHeaders([...headers, { key: "", value: "" }]);
  };

  const removeHeader = (index) => {
    const newHeaders = [...headers];
    newHeaders.splice(index, 1);
    setHeaders(newHeaders);
  };

  const updateHeader = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    setHeaders(newHeaders);
  };

  // Format schedule in human-readable form
  const getScheduleText = () => {
    if (!data.enabledTriggers || data.enabledTriggers.length === 0) {
      return "No triggers enabled";
    }

    if (schedule === "0 * * * *") {
      return "Every minute, every hour, every day";
    } else if (schedule === "0 0 * * *") {
      return "Every day at midnight";
    } else {
      return schedule;
    }
  };

  const handleEditTriggers = () => {
    // Get the parent component's onOpenTriggersPanel function from data
    if (data.onOpenTriggersPanel) {
      data.onOpenTriggersPanel("schedule-1", data.workflowId, id, enabledTriggers); // Pass workflowId, nodeId, and current triggers
    }
  };

  const toggleMinimize = () => {
    if (data.onNodeStateChange) {
      data.onNodeStateChange(id, !expanded);
    }
  };

  const handleOpenInTab = () => {
    // Open the triggers panel with the selected trigger
    if (data.onOpenTriggersPanel) {
      data.onOpenTriggersPanel("schedule-1", data.workflowId, id, enabledTriggers); // Pass workflowId, nodeId, and current triggers
    }
  };

  const handleConnectionClick = (event) => {
    // Only handle as a click if it wasn't a drag operation
    if (!isDragging.current) {
      event.preventDefault();
      event.stopPropagation();

      // Get the position of the click in screen coordinates
      const position = {
        x: event.clientX,
        y: event.clientY,
      };

      // Open the connection menu at the clicked position
      if (data.onOpenConnectionMenu) {
        data.onOpenConnectionMenu(position, id);
      }
    }

    // Reset dragging state
    isDragging.current = false;
  };

  return (
    <div
      className={`trigger-node-container trigger-node-outline dark:bg-card ${!expanded ? "minimized" : ""} ${
        isHovering ? "trigger-node-hovering" : ""
      }`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Node Header - This part should be draggable */}
      <div className="trigger-node-header dark:bg-card dark:border-border">
        <div className="trigger-node-title">
          <Zap size={16} className="trigger-icon" />
          <span>startTrigger</span>
        </div>
        <div className="trigger-node-controls">
          <CopyPasteButtons 
            nodeId={id}
            nodeType="triggerNode"
            nodeName="startTrigger"
            className="nodrag"
          />
          <div className="minimize-button-container nodrag">
            <button
              className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
              onClick={(e) => {
                e.stopPropagation();
                toggleMinimize();
              }}
              onMouseEnter={() => setShowMinimizeTooltip(true)}
              onMouseLeave={() => setShowMinimizeTooltip(false)}
            >
              {!expanded ? <Plus size={16} /> : <Minus size={16} />}
            </button>
            {showMinimizeTooltip && <div className="minimize-tooltip"></div>}
          </div>
          <div className="square-button-container nodrag">
            <button
              className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
              onMouseEnter={() => setShowSquareTooltip(true)}
              onMouseLeave={() => setShowSquareTooltip(false)}
              onClick={handleOpenInTab}
            >
              <Table2 size={16} />
            </button>
            {showSquareTooltip && (
              <div className="square-tooltip">Open in tab</div>
            )}
          </div>
          <button 
            className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
            onClick={async (e) => {
              e.stopPropagation()
              if (confirm('Are you sure you want to delete this trigger node?')) {
                try {
                  if (deleteNode && currentWorkflow) {
                    await deleteNode(currentWorkflow.id, id, null) // CSRF token not needed for trigger nodes
                  } else {
                    throw new Error('Unable to delete node: missing deleteNode function or currentWorkflow')
                  }
                } catch (error) {
                  console.error('Failed to delete trigger node:', error)
                  alert('Failed to delete trigger node. Please try again.')
                }
              }
            }}
            title="Delete trigger node"
            style={{ color: '#ef4444' }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Node Content - Should not be draggable and only visible when not minimized */}
      {expanded && (
        <div className="trigger-node-content nodrag">
          {/* Enabled Triggers Section */}
          <div className="trigger-section nodrag">
            <h3 className="trigger-section-title nodrag dark:text-white">Enabled triggers</h3>
            {enabledTriggers && enabledTriggers.length > 0 ? (
              <div className="enabled-triggers-list nodrag dark:bg-[#1f2937] dark:border-none">
                {enabledTriggers.map((trigger) => (
                  <span
                    key={trigger.id}
                    className="enabled-trigger-pill nodrag "
                  >
                    {trigger.name || trigger.type}
                  </span>
                ))}
              </div>
            ) : (
              <div className="no-triggers-message nodrag dark:bg-[#1f2937] dark:border-none">
                No triggers enabled
              </div>
            )}
            <button
              className="edit-triggers-button nodrag dark:hover:bg-[#1f2937]"
              onClick={handleEditTriggers}
            >
              <Edit2 size={14} />
              <span>Edit triggers</span>
            </button>
          </div>
        </div>
      )}

      {/* Output handle at the top-right */}
      {/* <div
        className="output-handle-container nodrag"
        onMouseEnter={() => {
          setShowTooltip(true);
          setIsHovering(true);
          // Add a class to the body to indicate that panning should be disabled
          document.body.classList.add("disable-flow-panning");
        }}
        onMouseLeave={() => {
          setShowTooltip(false);
          // Remove the class from the body to re-enable panning
          document.body.classList.remove("disable-flow-panning");
          // Don't set isHovering to false here to avoid conflicts with the parent hover
        }}
      >
        {showTooltip && (
          <div className="connection-tooltip nodrag">
            Click/drag to connect next block
          </div>
        )} */}
        <Handle
          type="source"
          position={
            // data.autoLayoutMode && data.handlePosition === 'bottom' 
            //   ? Position.Bottom 
            //   : (currentWorkflow?.viewMode === "tree" ? Position.Bottom : Position.Right)
            Position.Bottom
          }
          id="a"
          // className={`connection-handle output-handle nodrag ${
          //   isHovering ? "hovering" : ""
          // }`}
        />
      {/* </div> */}
    </div>
  );
};

export default TriggerNode;
